import React, {
    useContext
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    Sparkles, Layout, ArrowRight
} from 'lucide-react';
import {
    Card, CardContent
} from 'shared/components/ui/card';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    APP_STUDIO_ENABLED
} from 'shared/config/featureFlags';

/*
 * THE TWO STUDIOS, and nothing else on the page. The legacy App Builder tile
 * was removed on 2026-09-22 (the route `/organizations/:id/app-builder` still
 * answers for anyone who has it bookmarked — only the door on this page is
 * gone). With two choices the page reads as a choice, so both tiles are large:
 * the icon sits in a tinted square, the title is a real heading and the
 * description is body size, not a caption.
 */
function StudioPicker() {
    const navigate = useNavigate();
    const {
        oragID
    } = useParams();
    const {
        selectedOrganization
    } = useContext(OrganizationContext);

    const pickDEStudio = () => {
        navigate(`/organizations/${oragID}/sub`);
    };

    const pickAppStudio = () => {
        navigate(`/organizations/${oragID}/3dapp-studio`);
    };

    return (
        <div className="flex flex-col items-center justify-center w-full min-h-[70vh] p-8">
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold tracking-tight mb-3">Choose a Studio</h1>
                <p className="text-lg text-muted-foreground">
                    Pick which studio you want to work in for {selectedOrganization?.name || 'this organization'}.
                </p>
            </div>
            <div className={`grid grid-cols-1 ${APP_STUDIO_ENABLED ? 'md:grid-cols-2' : ''} gap-8 w-full max-w-5xl`}>
                <StudioCard
                    icon={<Sparkles className="h-10 w-10 text-blue-500" />}
                    tint="bg-blue-500/10"
                    title="Digital Employee Studio"
                    description="Build the Digital Workforce that gets work done."
                    onClick={pickDEStudio}
                />
                {APP_STUDIO_ENABLED && (
                    <StudioCard
                        icon={<Layout className="h-10 w-10 text-purple-500" />}
                        tint="bg-purple-500/10"
                        title="3D Apps Studio"
                        description="Data-driven Decisioning (3D) — Turn data into decisions, and decisions into action."
                        onClick={pickAppStudio}
                    />
                )}
            </div>
        </div>
    );
}

function StudioCard({
    icon, tint, title, description, onClick
}) {
    return (
        <Card
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
            className="group cursor-pointer hover:shadow-xl transition-all duration-200 border-2 hover:border-primary hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
            <CardContent className="p-10 flex flex-col items-start gap-5 min-h-[16rem] h-full">
                <div className={`flex items-center justify-center h-20 w-20 rounded-2xl ${tint}`}>
                    {icon}
                </div>
                <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
                <p className="text-lg leading-relaxed text-muted-foreground">{description}</p>
                <span className="mt-auto inline-flex items-center gap-1.5 text-base font-medium text-muted-foreground group-hover:text-primary group-focus-visible:text-primary transition-colors">
                    Open
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </span>
            </CardContent>
        </Card>
    );
}

export default StudioPicker;
